import qs from 'qs';
import moment from 'moment';
import api from './api';
import generateSearchRequest from '../utils/sequelize-search-builder-client';
import { normalizeCommercialOrder } from './commercialOrder';

const selectSubscription = (subscription) => {
	return {
		...subscription,
		createdAt: subscription.createdAt ? moment(subscription.createdAt).toDate() : null,
		updatedAt: subscription.updatedAt ? moment(subscription.updatedAt).toDate() : null,
		startingDate: subscription.startingDate ? moment(subscription.startingDate).toDate() : null,
		closingDate: subscription.closingDate ? moment(subscription.closingDate).toDate() : null,
	};
};

const selectSubscriptions = (subscriptions) => subscriptions.map(selectSubscription);

const PAY_EXTERNALLY_DEFAULT_ERROR = 'Erro ao pagar fatura. Tente novamente mais tarde.';
const PAY_EXTERNALLY_ASYNC_ERROR =
	'Pagamento externo retornou processamento assíncrono (202/jobId). O sucesso final exige status 200 com body da invoice.';

const buildPayExternallyError = (message, status) => {
	const normalizedError = new Error(message || PAY_EXTERNALLY_DEFAULT_ERROR);
	normalizedError.status = status ?? null;
	return normalizedError;
};

const getPayExternallyErrorMessage = (error) => {
	return (
		error?.response?.data?.error ||
		error?.response?.data?.message ||
		error?.message ||
		PAY_EXTERNALLY_DEFAULT_ERROR
	);
};

const parsePayExternallyResponse = (response, endpointLabel) => {
	const status = response?.status;
	const body = response?.data?.body;
	const hasJobId =
		typeof body === 'object' && body !== null && Object.prototype.hasOwnProperty.call(body, 'jobId');

	if (status === 200 && body && !hasJobId) {
		return body;
	}

	if (status === 202 || hasJobId) {
		throw buildPayExternallyError(PAY_EXTERNALLY_ASYNC_ERROR, status || 202);
	}

	throw buildPayExternallyError(
		`Resposta inesperada ao pagar fatura (${endpointLabel}). Status: ${status || 'desconhecido'}.`,
		status,
	);
};

const callGenericPayExternally = async (invoiceId, externalPaymentId, externalPaymentDescription) => {
	try {
		const response = await api.post(`/invoices/${invoiceId}/pay-externally`, {
			externalPaymentId,
			externalPaymentDescription,
		});
		return parsePayExternallyResponse(response, 'novo endpoint');
	} catch (error) {
		if (error?.status && !error?.response) {
			throw error;
		}

		console.log(
			'fail when trying to pay invoice externally (generic) in SubscriptionService.',
			error.response?.data,
		);
		throw buildPayExternallyError(getPayExternallyErrorMessage(error), error?.response?.status);
	}
};

const callLegacyPayExternally = async (
	subscriptionId,
	invoiceId,
	externalPaymentId,
	externalPaymentDescription,
	isPurchase,
) => {
	try {
		const response = await api.post(
			`/subscriptions/${subscriptionId}/invoices/${invoiceId}/pay-externally/${isPurchase}`,
			{
				externalPaymentId,
				externalPaymentDescription,
			},
		);
		return parsePayExternallyResponse(response, 'endpoint legado');
	} catch (error) {
		if (error?.status && !error?.response) {
			throw error;
		}

		console.log(
			'fail when trying to pay invoice externally (legacy) in SubscriptionService.',
			error.response?.data,
		);
		throw buildPayExternallyError(getPayExternallyErrorMessage(error), error?.response?.status);
	}
};

class SubscriptionService {
	async getAll({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				clientId: {
					as: 'clientId',
					type: 'equal',
				},
				clientName: {
					as: 'client.name',
					type: 'like',
				},
				clientCpf: {
					as: 'client.cpf',
					type: 'like',
				},
				clientDependent: {
					as: 'client.depedent',
					type: 'like',
				},
				planName: {
					as: 'plan.name',
					type: 'like',
				},
				planId: {
					as: 'planId',
					type: 'equal',
				},
				isActive: {
					as: 'isActive',
					type: 'equal',
				},
				hash: {
					as: 'hash',
					type: 'like',
				},
				providerKey: {
					as: 'providerKey',
					type: 'like',
				},
				providerName: {
					as: 'providerName',
					type: 'like',
				},
				creditcardToken: {
					as: 'creditcardToken',
					type: 'like',
				},
				status: {
					as: 'status',
					type: 'like',
				},
			},
			order: normalizeCommercialOrder(order),
		};
		const request = generateSearchRequest(filters, settings);
		return api
			.get('/subscriptions', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: {
					page,
					size,
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectSubscriptions(data.body) }))
			.catch((error) => {
				console.log(
					'fail when trying to get subscriptions in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async create(subscriptionForm) {
		console.log({ subscriptionForm });
		return api
			.post(`/plans/${subscriptionForm.planId}/purchase`, subscriptionForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to create subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async createPaymentToken(creditCardInfo) {
		return new Promise((resolve, reject) => {
			if (!window.Iugu) {
				reject(new Error(`Iugu.js not loaded`));
			}

			window.Iugu.createPaymentToken(
				{
					number: creditCardInfo.number,
					verification_value: creditCardInfo.verificationValue,
					month: creditCardInfo.month,
					year: creditCardInfo.year,
					first_name: creditCardInfo.firstName,
					last_name: creditCardInfo.lastName,
				},
				(response) => {
					if (response.errors) {
						console.log('createPaymentToken errors:', response.errors);
						reject(new Error(`It was no possible to create a payment token`));
					} else {
						resolve(response.id);
					}
				},
			);
		});
	}

	async getById(id) {
		return api
			.get(`/subscriptions/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to get subscription by id in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getStatuses() {
		return api
			.get('/subscriptions/statuses')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get subscriptions statuses in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	/**
	 * @param {number} clientId - ID of the client
	 * @param {object} param1 - Pagination and filter parameters
	 * @param {number} param1.page - Page number (0-based)
	 * @param {number} param1.size - Number of items per page
	 * @param {string|null} param1.statusFilter - (Optional) Status to filter invoices
	 * @param {string|null} subscriptionId - (Optional) ID of the subscription to filter invoices
	 * @returns {Promise} - Promise resolving to the list of invoices
	 */
	async getInvoices(clientId, { page = 0, size = 10, statusFilter = null }, subscriptionId = null) {
		const params = {
			page,
			size,
		};

		if (subscriptionId) {
			params.subscriptionId = subscriptionId;
		}

		if (statusFilter) {
			params.statusFilter = statusFilter;
		}

		return api
			.get(`/subscriptions/${clientId}/invoices`, {
				params,
			})
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying to get invoices in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async activate(subscriptionId) {
		return api
			.post(`/subscriptions/${subscriptionId}/activate`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to activate subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async suspend(subscriptionId) {
		return api
			.post(`/subscriptions/${subscriptionId}/suspend`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to suspend subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	/**
	 * Agenda o cancelamento em background (a API responde 202 com o job).
	 * @returns {Promise<{ jobId: number, status: string, dedupeHit: boolean }>}
	 */
	async cancel(subscriptionId) {
		return api
			.post(`/subscriptions/${subscriptionId}/cancel`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to cancel subscription in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	/**
	 * @param {number} jobId - ID do job assíncrono
	 * @returns {Promise<{ id: number, status: string, nextAttemptAt: string|null, errorMessage: string|null }>}
	 */
	async getJobStatus(jobId) {
		return api
			.get(`/jobs/${jobId}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get job status in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async payInvoiceExternally(
		subscriptionId,
		invoiceId,
		externalPaymentId,
		externalPaymentDescription,
		isPurchase = false,
	) {
		// Always prioritize the generic endpoint.
		try {
			return await callGenericPayExternally(
				invoiceId,
				externalPaymentId,
				externalPaymentDescription,
			);
		} catch (error) {
			const status = error?.status ?? error?.response?.status;
			const shouldFallbackLegacy = (status === 404 || status === 405) && subscriptionId;
			if (!shouldFallbackLegacy) {
				throw error;
			}
		}

		return callLegacyPayExternally(
			subscriptionId,
			invoiceId,
			externalPaymentId,
			externalPaymentDescription,
			isPurchase,
		);
	}

	async payInvoiceExternallyGeneric(invoiceId, externalPaymentId, externalPaymentDescription) {
		// New generic endpoint that works for all invoice types
		return callGenericPayExternally(invoiceId, externalPaymentId, externalPaymentDescription);
	}

	async createNewInvoice(invoiceForm) {
		return api
			.post('/invoices', invoiceForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying create a new invoice externally in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getProviderinfo(subscriptionId) {
		return api
			.get(`/subscriptions/${subscriptionId}/provider-info`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get provider info in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getInfoClientIugu(clientId) {
		return api
			.get(`/subscriptions/iugu/${clientId}`)
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying to get info client IUGU in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getSubscriptionByClientId(clientId) {
		return api
			.get(`/subscriptions/clientId/${clientId}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get subscription by client id SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getContractedPlanByClientId(clientId) {
		return api
			.get(`/clients/${clientId}/contracted-plan`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get contracted plan by client id SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async updatePaymentMehotdSubscriptionIUGU({ subscriptionId, payableWith }) {
		return api
			.post('/subscriptions/iugu/update/subscription', { subscriptionId, payableWith })
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying update subscription IUGU in subscrpition service.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async updatePlanSubscriptionIUGU({
		subscriptionIuguId = null,
		planId = null,
		clientId = null,
		dueDate,
	}) {
		return api
			.post('/subscriptions/iugu/update/plan/subscription', {
				subscriptionIuguId,
				planId,
				clientId,
				dueDate,
			})
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					'fail when trying update subscription IUGU in subscrpition service.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getByContractId(contractId) {
		return api
			.get(`/subscriptions/contract/${contractId}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectSubscription)
			.catch((error) => {
				console.log(
					'fail when trying to get subscription by contract id in SubscriptionService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
